import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PayButton from "../../components/PayButton/PayButton";

// Styled components for the dialog
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const CloseButton = styled(IconButton)({
  color: '#ffffff',
});

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&.MuiButton-outlined': {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(31, 122, 84, 0.04)',
      borderColor: theme.palette.primary.dark,
    },
  },
}));

const InfoBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
}));

const DuePaymentModal = ({
  open,
  onClose,
  onSubmit,
  dueAmount,
  memberId,
  paymentId,
}) => {
  const [discount, setDiscount] = useState("");
  const [payingAmount, setPayingAmount] = useState(dueAmount || "");
  const [modeOfPayment, setModeOfPayment] = useState("");
  const [dateOfPayment, setDateOfPayment] = useState(dayjs());
  const [error, setError] = useState("");
  useEffect(() => {
    setDiscount("");
    setPayingAmount(dueAmount || "");
  }, [dueAmount]);
  useEffect(() => {
    if (open) {
      setDateOfPayment(dayjs());
      setDiscount("");
      setPayingAmount(dueAmount || "");
      setError("");
    }
  }, [open]);

  const discountValue = Number(discount) || 0;

  const handleDiscountChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (Number(value || 0) <= Number(dueAmount || 0)) {
      setDiscount(value);
      // Auto-fill the amount to collect as due minus discount; the user can still
      // lower it further afterward to leave part of it as a remaining due.
      setPayingAmount(String(Math.max(0, Number(dueAmount || 0) - Number(value || 0))));
      setError("");
    }
  };

  const handlePayingAmountChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (Number(value || 0) + discountValue <= Number(dueAmount || 0)) {
      setPayingAmount(value);
      setError("");
    }
  };

  const handleSubmit = () => {
    const paying = Number(payingAmount) || 0;
    if (paying <= 0) {
      setError("Amount to collect must be greater than 0");
      return;
    }
    if (paying + discountValue > Number(dueAmount || 0)) {
      setError("Amount to collect + discount cannot be greater than the due amount");
      return;
    }
    setError("");
    // Format the date to 'yyyy-mm-dd' for submission
    const formattedDate = dateOfPayment.format("YYYY-MM-DD");

    onSubmit({
      member_id: memberId,
      payment_id: paymentId,
      paying_amount: paying,
      discount: discountValue,
      mode_of_payment: modeOfPayment,
      date_of_payment: formattedDate,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        Due Payment Details
        <CloseButton edge="end" onClick={onClose} aria-label="close">
          <CloseIcon />
        </CloseButton>
      </StyledDialogTitle>
      <StyledDialogContent sx={{mt:2}}>
        <InfoBox>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Payment Information
          </Typography>
          <TextField
            fullWidth
            label="Due Amount"
            value={dueAmount}
            margin="normal"
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            fullWidth
            label="Discount"
            value={discount}
            onChange={handleDiscountChange}
            margin="normal"
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Amount to Collect"
            value={payingAmount}
            onChange={handlePayingAmountChange}
            margin="normal"
            variant="outlined"
            error={!!error}
            helperText={error}
          />
          <TextField
            fullWidth
            select

            value={modeOfPayment}
            onChange={(e) => setModeOfPayment(e.target.value)}
            margin="normal"
            variant="outlined"
            SelectProps={{
              native: true,
            }}
          >
            <option value="">Select Payment Mode</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="upi">UPI</option>
            <option value="netbanking">Net Banking</option>
          </TextField>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="en-gb"
          >
            <DatePicker
              label="Date of Payment"
              format="DD/MM/YYYY"
              value={dateOfPayment}
              onChange={(d) => d && setDateOfPayment(d)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "normal",
                  variant: "outlined",
                },
              }}
            />
          </LocalizationProvider>
        </InfoBox>
      </StyledDialogContent>
      <DialogActions sx={{ padding: 2, gap: 1 }}>
        <StyledButton 
          variant="outlined"
          onClick={onClose}
          startIcon={<CancelIcon />}
        >
          Cancel
        </StyledButton>
        <PayButton overlay ButtonComponent={StyledButton}
          variant="contained"
          onClick={handleSubmit}
          startIcon={<CheckCircleIcon />}
        >
          Submit Payment
        </PayButton>
      </DialogActions>
    </Dialog>
  );
};

export default DuePaymentModal;
